import React, { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Avatar from "./Avatar";

// Motion props as a module constant — referentially stable across renders.
const PREVIEW_MOTION = {
    initial: { opacity: 0, y: 6, scale: 0.92 },
    animate: { opacity: 1, y: 0, scale: 1 },
    exit: { opacity: 0, y: 4, scale: 0.95 },
    transition: { type: "spring", stiffness: 280, damping: 22 },
};

// UnlockPreviewCard — wraps a Settings unlock card and pops a tiny Mochi
// "closet preview" while the user presses-and-holds the card.
//
//   <UnlockPreviewCard preview={{ skin: "sunglasses" }}>{cardChildren}</UnlockPreviewCard>
//
// preview shape (one of):
//   { skin: "sunglasses" }       — Mochi puts on shades
//   { accessory: "crown" }       — gold crown floats on Mochi's head
//   { accessory: "birthday-hat" } — birthday hat
//   undefined / null              — card is non-previewable, renders kids as-is
//
// Interaction model:
//   - pointerdown   → start hold
//   - pointerup, pointerleave, pointercancel → end hold
//   - 180ms grace before showing prevents flicker on stray taps
//
// The preview is rendered as a popover ABOVE the card with a soft cream
// background + clay shadow. The actual milestone unlock window in the user's
// settings is untouched — this is a pure visual try-on.
export default function UnlockPreviewCard({ children, preview, locked, avatarStyle = "feminine", testId }) {
    const [active, setActive] = useState(false);
    const timerRef = useRef(null);

    const previewable = !!preview && !locked;

    const start = () => {
        if (!previewable) return;
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setActive(true), 180);
    };
    const end = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        setActive(false);
    };

    // Build the synthetic settings object the MochiAccessory hook reads from.
    // We force the `_until` timestamp into the far future ONLY for this
    // preview — none of this is persisted to the backend.
    const previewAccessory = preview?.accessory || null;

    return (
        <div
            className="relative"
            onPointerDown={start}
            onPointerUp={end}
            onPointerLeave={end}
            onPointerCancel={end}
            data-testid={testId}
        >
            {children}

            <AnimatePresence>
                {active && (
                    <motion.div
                        {...PREVIEW_MOTION}
                        className="absolute left-1/2 -translate-x-1/2 -top-2 -translate-y-full z-30 pointer-events-none"
                        data-testid={`${testId}-preview`}
                    >
                        <div className="relative bg-cream rounded-2xl shadow-clay border border-warm-300/30 p-2">
                            <div className="relative w-[110px] h-[110px] mx-auto">
                                <Avatar
                                    mood="happy"
                                    size={110}
                                    style={avatarStyle}
                                    skin={preview?.skin || null}
                                    accessory={previewAccessory}
                                    pin={!!preview?.pin}
                                    accessoryAnimateIn
                                />
                            </div>
                            <p className="font-body text-[10px] text-warm-500 text-center mt-1 italic">try on</p>
                            {/* Notch pointing down to the card */}
                            <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-cream rotate-45 border-r border-b border-warm-300/30" />
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
